import React, { useEffect, useState } from "react";
import { ChapterArticle as ChapterArticleModel, getChapterArticle } from "../api/cocApi";
import { RouteComponentProps } from "react-router-dom";
import PatternBackground from "../components/PatternBackground/PatternBackground";
import HighlightBox from "../components/HighlightBox/HighlightBox";
import Container from "../components/Container/Container";
import Intro from "../components/Intro/Intro";
import ArticleHeader from "../components/ArticleHeader/ArticleHeader";
import Spacer from "../components/Spacer/Spacer";
import ArticleSection from "../components/ArticleSection/ArticleSection";
import QuestionAnswer from "../components/QuestionAnswer/QuestionAnswer";
import LinkList from "../components/LinkList/LinkList";
import ArticleNav from "../components/ArticleNav/ArticleNav";
import CollapseContent from "../components/CollapseContent/CollapseContent";
import FullWidthTeaser from "../components/FullWidthTeaser/FullWidthTeaser";
import LinkedListNode from "ts-linked-list/dist/LinkedListNode";
import Media from "react-media";

export interface Props extends RouteComponentProps<{ slug?: string }> {}

export default function ChapterArticle(props: Props) {
    const [chapterArticleNode, setChapterArticleNode] = useState<LinkedListNode<ChapterArticleModel>>();

    useEffect(() => {
        if (props.match.params.slug) {
            getChapterArticle(props.match.params.slug).then(c => setChapterArticleNode(c));
        }
    }, [props.match.params.slug]);

    if (chapterArticleNode == null) {
        return null;
    }

    const chapterArticle = chapterArticleNode.data;

    return (
        <>
            <section>
                <ArticleHeader
                    heading={chapterArticle.heading}
                    preHeading={chapterArticle.tocIndex}
                    image={chapterArticle.image}
                    backgroundImage={chapterArticle.backgroundImage}
                    onClose={() => props.history.push("/")}
                />

                <PatternBackground>
                    <Container>
                        <Intro content={chapterArticle.introduction} />
                    </Container>
                </PatternBackground>

                <Container>
                    <Spacer size="Spacer--large">
                        <HighlightBox heading="Key takeaways" content={chapterArticle.keyTakeaways} />
                    </Spacer>
                </Container>

                <Container>
                    <ArticleSection heading="What we stand for">
                        <div dangerouslySetInnerHTML={{ __html: chapterArticle.standFor }} />
                    </ArticleSection>

                    <ArticleSection heading="What this means for me">
                        <h3>As an employee I am expected to</h3>
                        <Media queries={{ smallMedium: "(max-width: 1024px)" }}>
                            {matches => (
                                <CollapseContent enabled={matches.smallMedium}>
                                    <div dangerouslySetInnerHTML={{ __html: chapterArticle.meaning.asEmployee }} />
                                </CollapseContent>
                            )}
                        </Media>

                        <h3 className="u-marginT3">In addition, as a leader I am expected to</h3>
                        <Media queries={{ smallMedium: "(max-width: 1024px)" }}>
                            {matches => (
                                <CollapseContent enabled={matches.smallMedium}>
                                    <div dangerouslySetInnerHTML={{ __html: chapterArticle.meaning.asLeader }} />
                                </CollapseContent>
                            )}
                        </Media>
                    </ArticleSection>

                    <ArticleSection heading="How should I act?">
                        {chapterArticle.howToAct.map(faq => (
                            <QuestionAnswer key={faq.question} {...faq} />
                        ))}
                    </ArticleSection>

                    <ArticleSection heading="Where can I learn more?">
                        <LinkList links={chapterArticle.relatedMaterial} />
                    </ArticleSection>
                </Container>

                {chapterArticle.definitions ? (
                    <PatternBackground overflow>
                        <Container>
                            <ArticleSection heading="Definitions" headingImage={chapterArticle.stampImage} borderless>
                                <div dangerouslySetInnerHTML={{ __html: chapterArticle.definitions }} />
                            </ArticleSection>
                        </Container>
                    </PatternBackground>
                ) : null}
            </section>

            <ArticleNav nextArticle={chapterArticleNode.next?.data} previousArticle={chapterArticleNode.prev?.data} />

            <FullWidthTeaser
                backgroundImage={process.env.PUBLIC_URL + "/assets/Speakup.jpg"}
                heading="Speak up"
                body="Suspect a violation of the Code of Conduct? Report it to a manager or through our anonymous Whistleblower function."
                linkText="Read more"
                linkTo="/speak-up"
            />
        </>
    );
}
